import { useAtom } from "jotai";
import { SaveState } from "models/enumerations/save-state";
import { CodeReferenceBlockConfigurationRecord } from "models/view-models/block-configurations/code-reference-block-configuration-record";
import CodeReferencePreview from "organisms/code-reference-block-editor/code-reference-preview";
import { PageBlockEditorProps } from "organisms/page-blocks/active-page-block/page-block-editor";
import SectionDropZone from "organisms/section-drag-and-drop/section-drop-zone";
import useToggleNotebookReferencePanel from "pages/notebooks/notebook-reference-panel/use-toggle-notebook-reference-panel";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DroppedSection } from "utilities/atoms/section-drag-and-drop/dropped-section";
import usePageBlockError from "utilities/hooks/domain/notebooks/pages/use-page-block-errors";
import { t } from "utilities/localization-utils";
import CodeReferenceBlockConfigurationService, {
    CodeReferenceBlockConfigurationUpdatePathParams,
    CodeReferenceBlockConfigurationUpdatePutParams,
} from "utilities/services/block-configurations/code-reference-block-configuration-service";
import { isCodeReferenceAdditionalData } from "models/interfaces/additional-block-data/additional-code-reference-block-data";
import CodeReferenceBlockToolbar from "./code-reference-block-toolbar";
import useNotebookBreakpoints from "utilities/hooks/domain/notebooks/use-notebook-breakpoints";
import { useCodeReferenceHighlightsApi } from "utilities/hooks/domain/notebooks/code-reference-blocks/use-code-reference-highlights-api";
import { RecordUtils } from "@rsm-hcd/javascript-core";
import useUserSettings from "utilities/hooks/domain/user-settings/use-user-settings";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { UserSettingsKeys } from "constants/user-settings-keys";
import UserSettingRecord from "models/view-models/user-setting-record";

interface CodeReferenceBlockEditorProps
    extends Pick<
        PageBlockEditorProps,
        | "additionalBlockData"
        | "block"
        | "onBlockSaving"
        | "onBlockSaved"
        | "onDone"
        | "onDelete"
        | "onMoveDown"
        | "onMoveUp"
    > {}

const CSS_CLASS_NAME = "c-code-reference-block";

const CodeReferenceBlockEditor: React.FC<CodeReferenceBlockEditorProps> = ({
    additionalBlockData,
    block,
    onBlockSaved,
    onBlockSaving,
    onDone,
    onDelete,
    onMoveDown,
    onMoveUp,
}: CodeReferenceBlockEditorProps) => {
    const configuration = useMemo(
        () =>
            RecordUtils.ensureRecord(
                block.configuration,
                CodeReferenceBlockConfigurationRecord
            ),
        [block.configuration]
    );
    const sectionToRender = useMemo(() => {
        if (!isCodeReferenceAdditionalData(additionalBlockData)) {
            return null;
        }

        return additionalBlockData.section;
    }, [additionalBlockData]);
    const [sectionToAdd, setSectionToAdd] = useAtom(DroppedSection);
    const [saveState, setSaveState] = useState<SaveState>(SaveState.NotSaving);
    const [isEnhancedContentDisplayed, setIsEnhancedContentDisplayed] =
        useState<boolean>(configuration.isEnhancedContentDisplayed ?? false);
    const { update: updateApi } =
        CodeReferenceBlockConfigurationService.useUpdate();
    const { createOrUpdate } = useUserSettings();
    const { globalState, setGlobalState } = useGlobalState();

    const notebookEnhancedContentSetting = useMemo(
        () =>
            globalState.currentIdentity?.userSettings.find(
                (setting) =>
                    setting.key === UserSettingsKeys.NotebookEnhancedContentShow
            ),
        [globalState.currentIdentity?.userSettings]
    );

    const { showError } = usePageBlockError();
    const { createHighlight, deleteHighlight, updateHighlight } =
        useCodeReferenceHighlightsApi({
            block,
            handleFinished: (block?) => {
                if (block) onBlockSaved(block);
            },
        });

    const {
        showPanel,
        closePanel,
        toggleDisplay: toggleReferencePanel,
    } = useToggleNotebookReferencePanel();
    const { isMobile } = useNotebookBreakpoints();

    const handleUpdateCodeReferenceBlockConfiguration = useCallback(
        async (
            updateParams: CodeReferenceBlockConfigurationUpdatePutParams
        ) => {
            setSaveState(SaveState.Saving);
            onBlockSaving();

            if (!configuration.id) return;
            try {
                const pathParams: CodeReferenceBlockConfigurationUpdatePathParams =
                    {
                        configurationId: configuration.id,
                    };

                const response = await updateApi(updateParams, pathParams);

                if (response.result == null || response.resultObject == null) {
                    throw new Error(
                        `No response when updating code reference block configuration with ID: ${configuration.id}`
                    );
                }

                if (response.result.hasErrors()) {
                    throw new Error(
                        response.result.listErrorMessages().join(", ")
                    );
                }

                const updatedConfiguration = response.resultObject;

                const updatedBlock = block.with({
                    configuration: updatedConfiguration,
                });

                onBlockSaved(updatedBlock);
                closePanel();
            } catch (error) {
                showError("updating", "block");

                onBlockSaved(block);
            } finally {
                setSaveState(SaveState.Saved);
            }
        },
        [
            block,
            closePanel,
            configuration.id,
            onBlockSaved,
            onBlockSaving,
            showError,
            updateApi,
        ]
    );

    const addSectionAndResetZone = useCallback(async () => {
        if (sectionToAdd?.id == null || saveState) {
            return;
        }

        const showEnhancedContent =
            notebookEnhancedContentSetting?.getValue<boolean>() ?? true;

        if (showEnhancedContent !== isEnhancedContentDisplayed) {
            setIsEnhancedContentDisplayed(showEnhancedContent);
        }

        await handleUpdateCodeReferenceBlockConfiguration({
            sectionId: sectionToAdd.id,
            isEnhancedContentDisplayed: showEnhancedContent,
        });
    }, [
        sectionToAdd?.id,
        saveState,
        notebookEnhancedContentSetting,
        isEnhancedContentDisplayed,
        handleUpdateCodeReferenceBlockConfiguration,
    ]);

    const updateUserSetting = async (isEnhancedContentDisplayed: boolean) => {
        const userId = globalState.currentIdentity?.userId();
        if (userId == null) return;

        const userSetting =
            notebookEnhancedContentSetting ??
            new UserSettingRecord({
                key: UserSettingsKeys.NotebookEnhancedContentShow,
                value: "true",
                type: "boolean",
            });

        const updatedUserSetting = userSetting.with({
            value: isEnhancedContentDisplayed.toString(),
        });

        try {
            const setting = await createOrUpdate(updatedUserSetting, userId);
            if (setting == null) {
                console.warn(
                    "Failed to update user setting for showing NFPA Insights in code reference blocks."
                );
                return;
            }

            setGlobalState((prev) => prev.updateUserSetting(setting));
        } catch (error) {
            console.warn(
                "Error updating user setting for showing NFPA Insights in code reference blocks: ",
                error
            );
        }
    };

    const handleToggleEnhancedContentDisplay = async () => {
        const newValue = !isEnhancedContentDisplayed;
        setIsEnhancedContentDisplayed(newValue);

        await handleUpdateCodeReferenceBlockConfiguration({
            isEnhancedContentDisplayed: newValue,
            sectionId: configuration.sectionId,
        });

        // Purposefully not awaited to avoid blocking UI
        updateUserSetting(newValue);
    };

    useEffect(() => {
        addSectionAndResetZone();
    }, [addSectionAndResetZone]);

    useEffect(() => {
        if (
            sectionToRender !== undefined &&
            sectionToAdd !== undefined &&
            sectionToRender?.id === sectionToAdd.id &&
            saveState === SaveState.Saved
        ) {
            setSectionToAdd(undefined);
        }
    }, [sectionToRender, sectionToAdd, setSectionToAdd, saveState]);

    useEffect(() => {
        if (!showPanel && !isMobile && !sectionToRender) {
            toggleReferencePanel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = () => {
        if (!isMobile) {
            return;
        }
        toggleReferencePanel();
    };

    const handleDone = async () => {
        await addSectionAndResetZone();
        onDone?.();
    };

    const handleDelete = async () => {
        await onDelete(block);
    };

    const handleMoveUp = async () => {
        await onMoveUp(block);
    };

    const handleMoveDown = async () => {
        await onMoveDown(block);
    };

    return (
        <>
            <CodeReferenceBlockToolbar
                isEnhancedContentDisplayed={isEnhancedContentDisplayed}
                onToggleEnhancedContentDisplay={
                    handleToggleEnhancedContentDisplay
                }
                onDelete={handleDelete}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                onDone={handleDone}
            />
            <div
                className={`${CSS_CLASS_NAME} ${saveState === SaveState.NotSaving ? "" : "-saved"}`}
                onClick={handleClick}
                data-test-id="page--code-reference-block--editor">
                {sectionToAdd || sectionToRender ? (
                    <CodeReferencePreview
                        isEnhancedContentDisplayed={
                            configuration.isEnhancedContentDisplayed
                        }
                        isEditMode
                        highlights={configuration.highlights}
                        onHighlight={createHighlight}
                        onUpdateHighlight={updateHighlight}
                        referenceSection={sectionToAdd || sectionToRender}
                        onDeleteHighlight={deleteHighlight}
                    />
                ) : (
                    <SectionDropZone
                        placeholderText={t("addCodeSectionFromReferencePanel")}
                    />
                )}
            </div>
        </>
    );
};

export default CodeReferenceBlockEditor;
